import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import Sheet from '../components/Sheet';
import { PRIMARY } from '../components/CaptureSheets';
import { SectionTitle } from '../components/SettingsRows';
import { SignOutButton } from '../components/AuthLinks';
import DeleteAccount from '../components/DeleteAccount';
import LaunchModeGroup from '../components/LaunchModeGroup';
import { FeedbackToggle } from '../components/FeedbackButton';
import { CoachTopBar } from './CoachShell';
import { vnShortDate } from '../lib/coach';
import { rememberMode } from '../lib/mode';
import { useApp, useAppPath } from '../lib/AppContext';

const ROW = 'flex min-h-16 w-full items-center gap-3 py-2 text-left';

function ProfileSheet({ open, gym, onClose, onSave }) {
  const path = useAppPath();
  const [value, setValue] = useState(gym || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) setValue(gym || ''); }, [open, gym]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSave(value.trim());
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open={open} onClose={onClose} title="Hồ sơ HLV">
      <form className="flex flex-col gap-3" onSubmit={save}>
        <label htmlFor="coach-gym" className="text-sm font-semibold">Nơi bạn dạy (học viên thấy khi kết nối)</label>
        <input id="coach-gym" className="input h-12 w-full rounded-[14px] border-line text-base" maxLength={80} placeholder="VD: Gym Tân Bình"
          value={value} onChange={(e) => setValue(e.target.value)} />
        <Link to={path('/profile')} className="text-sm font-semibold text-brand-text underline">Đổi tên và ảnh đại diện</Link>
        {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
        <button type="submit" className={PRIMARY} disabled={busy}>Lưu</button>
      </form>
    </Sheet>
  );
}

// A6 "Tài khoản" in coach mode: the coach profile, the way back to the personal app, where the app opens,
// the seat plan, the coach code, turning coach mode off, "Hiện nút góp ý", signing out and deleting the account.
export default function AccountTab() {
  const { api, coach, setCoach, profile } = useApp();
  const path = useAppPath();
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { api.get('/coach').then(setDetails).catch((e) => setError(e.message)); }, [api]);

  const update = async (body) => {
    const data = await api.patch('/coach', body);
    setDetails(data);
    setCoach((c) => ({ ...c, launch_mode: data.launch_mode, gym: data.gym, approval_required: data.approval_required }));
  };
  const setLaunch = (mode) => update({ launch_mode: mode }).catch((e) => setError(e.message));
  const toPersonal = () => { rememberMode('personal'); navigate(path('/')); };
  const turnOff = async () => {
    if (!window.confirm('Tắt chế độ HLV? Mã HLV sẽ ngừng hoạt động.')) return;
    setError(null);
    try {
      await api.del('/coach');
      rememberMode('personal');
      setCoach(null);
      navigate(path('/'), { replace: true });
    } catch (e) {
      setError(e.message);
    }
  };

  const plan = details?.plan;
  const launch = details?.launch_mode || coach?.launch_mode;
  return (
    <div className="flex flex-col gap-3">
      <CoachTopBar />
      <h1 className="text-[26px] font-bold">Tài khoản</h1>

      <section className="flex items-center gap-3 rounded-[20px] bg-base-100 p-4">
        <span className="overflow-hidden rounded-full bg-[#15261f]"><Avatar name={profile?.name} url={profile?.avatar_url} size={52} /></span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-lg font-bold">Coach {profile?.name}</p>
          <p className="truncate text-sm text-muted">{details?.gym ? `PT tại ${details.gym}` : 'Chưa ghi nơi dạy'}</p>
        </div>
        <button type="button" className="min-h-11 shrink-0 px-1 text-sm font-bold text-brand-text" onClick={() => setEditing(true)}>Sửa hồ sơ HLV</button>
      </section>

      <button type="button" onClick={toPersonal}
        className="flex min-h-16 items-center gap-3 rounded-[20px] border-2 border-[#17804f]/40 bg-[#e3f1e9]/60 p-4 text-left">
        <span className="grid size-10 shrink-0 place-items-center overflow-hidden rounded-full bg-base-100"><Avatar name={profile?.name} url={profile?.avatar_url} size={40} /></span>
        <span className="min-w-0 flex-1">
          <span className="block font-bold text-[#17804f]">Chuyển sang app cá nhân</span>
          <span className="block text-xs text-muted">Nhật ký và thực đơn của bạn</span>
        </span>
        <Icon name="chevronRight" className="size-5 text-[#17804f]" />
      </button>

      <LaunchModeGroup value={launch} onSelect={setLaunch} />

      <SectionTitle>Huấn luyện viên</SectionTitle>
      <div className="divide-y divide-line rounded-[20px] bg-base-100 px-4">
        <Link to={path('/coach/seats')} className={ROW}>
          <span className="grid size-10 shrink-0 place-items-center rounded-[12px] bg-base-200"><Icon name="gift" className="size-5" /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] font-semibold">Gói HLV &amp; chỗ</span>
            <span className="block text-xs text-muted">{plan ? `${plan.seats_used}/${plan.seats} chỗ · ${plan.active ? `gia hạn ${vnShortDate(plan.period_ends_at)}` : 'đã hết hạn'}` : 'Chưa có chỗ tặng Pro'}</span>
          </span>
          <Icon name="chevronRight" className="size-4 text-muted" />
        </Link>
        <Link to={path('/coach/code')} className={ROW}>
          <span className="grid size-10 shrink-0 place-items-center rounded-[12px] bg-base-200"><Icon name="qr" className="size-5" /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] font-semibold">Mã HLV</span>
            <span className="block text-xs text-muted">{coach?.code} · {(details?.approval_required ?? coach?.approval_required) ? 'duyệt trước khi kết nối' : 'kết nối ngay'}</span>
          </span>
          <Icon name="chevronRight" className="size-4 text-muted" />
        </Link>
        <button type="button" className={ROW} onClick={turnOff}>
          <span className="grid size-10 shrink-0 place-items-center rounded-[12px] bg-[#fdebe4] text-error"><Icon name="x" className="size-5" /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] font-semibold text-error">Tắt chế độ HLV</span>
            <span className="block text-xs text-muted">Chỉ tắt được khi không còn học viên</span>
          </span>
          <Icon name="chevronRight" className="size-4 text-muted" />
        </button>
      </div>
      {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
      <SectionTitle>Hỗ trợ</SectionTitle>
      <div className="rounded-[20px] bg-base-100 px-4"><FeedbackToggle /></div>
      <div className="flex flex-col gap-2 pt-3">
        <div className="rounded-[20px] bg-base-100 px-4">
          <SignOutButton className="h-14 w-full text-[15px] font-semibold text-error" />
        </div>
        <DeleteAccount />
      </div>
      <ProfileSheet open={editing} gym={details?.gym} onClose={() => setEditing(false)} onSave={(gym) => update({ gym })} />
    </div>
  );
}
